import React, { useState } from 'react';
import { bool, arrayOf, number, shape } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER, propTypes } from '../../util/types';
import { ensureCurrentUser, ensureUser } from '../../util/data';
import { withViewport } from '../../util/uiHelpers';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  H2,
  H4,
  Page,
  AvatarLarge,
  NamedLink,
  Reviews,
  ButtonTabNavHorizontal,
  LayoutSideNavigation,
  ReviewRating,
  ExternalLink,
  ListingCard,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import css from './ProfilePage.module.css';
import ContactInfoSection from './ContactInfoSection';
import { ABOUT, LISTINGS } from '../../util/constants';
import CompanyInfoSection from './CompanyInfoSection';
import CompanyProfileSection from './CompanyProfileSection';
import DesignOptionsSection from './DesignOptionsSection';
import ProfessionalServicesSection from './ProfessionalServicesSection';
import { UserType } from '../../util/enums';
import PlotInformationSection from './PlotInformationSection';
import OffsiteCompanyProfileSection from './OffsiteCompanyProfileSection';
import HouseQueriesSection from './HouseQueriesSection';
import KeyInformationSection from './KeyInformationSection';
import DesignScopeSection from './DesignScopeSection';

const MAX_MOBILE_SCREEN_WIDTH = 768;

export const AsideContent = props => {
  const { user, displayName, isCurrentUser } = props;
  return (
    <div className={css.asideContent}>
      <AvatarLarge className={css.avatar} user={user} disableProfileLink />
      <div className={css.asideUserContent}>
        <H2 as="h1" className={css.mobileHeading}>
          {displayName ? (
            <FormattedMessage id="ProfilePage.mobileHeading" values={{ name: displayName }} />
          ) : null}
        </H2>
        {isCurrentUser ? (
          <NamedLink className={css.editLinkDesktop} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
          </NamedLink>
        ) : null}
        <ReviewRating rootClassName={css.ratings} />
      </div>
    </div>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const MobileReviews = props => {
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  return (
    <div className={css.mobileReviews}>
      <div className={css.mobileReviewsTabs}>
        <H4 as="h2" className={css.mobileReviewsTitle}>
          <FormattedMessage id="ProfilePage.aboutMeTitle" />
        </H4>
        <H4 as="h2" className={css.mobileReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsFromMyCustomersTitle"
            values={{ count: reviewsOfProvider.length }}
          />
        </H4>
        <H4 as="h2" className={css.mobileReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsAsACustomerTitle"
            values={{ count: reviewsOfCustomer.length }}
          />
        </H4>
      </div>
      <div className={css.mobileReviewsTabsContent}>
        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
        <Reviews reviews={reviewsOfProvider} />
        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
        <Reviews reviews={reviewsOfCustomer} />
      </div>
    </div>
  );
};

export const DesktopReviews = props => {
  const [tabSelected, setTabSelected] = useState(ABOUT);
  const {
    reviews,
    queryReviewsError,
    publicData,
    hasListings,
    listings,
    listingsContainerClasses,
  } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  const isReviewTypeProviderSelected = tabSelected === REVIEW_TYPE_OF_PROVIDER;
  const isReviewTypeCustomerSelected = tabSelected === REVIEW_TYPE_OF_CUSTOMER;
  const isAboutSelected = tabSelected === ABOUT;
  const isListingsSelected = tabSelected === LISTINGS;
  const desktopReviewTabs = [
    {
      text: (
        <>
          <FormattedMessage id="ProfilePage.aboutMeTitle" />
        </>
      ),
      selected: isAboutSelected,
      onClick: () => setTabSelected(ABOUT),
    },
    {
      text: (
        <>
          <FormattedMessage id="ProfilePage.listings" />
        </>
      ),
      selected: isListingsSelected,
      onClick: () => setTabSelected(LISTINGS),
    },
    {
      text: (
        <>
          <FormattedMessage
            id="ProfilePage.reviewsFromMyCustomersTitle"
            values={{ count: reviewsOfProvider.length }}
          />
        </>
      ),
      selected: isReviewTypeProviderSelected,
      onClick: () => setTabSelected(REVIEW_TYPE_OF_PROVIDER),
    },
    {
      text: (
        <>
          <FormattedMessage
            id="ProfilePage.reviewsAsACustomerTitle"
            values={{ count: reviewsOfCustomer.length }}
          />
        </>
      ),
      selected: isReviewTypeCustomerSelected,
      onClick: () => setTabSelected(REVIEW_TYPE_OF_CUSTOMER),
    },
  ];
  const commonProps = {
    publicData,
  };
  const { calendlyLink, userType } = publicData || {};

  const userTypeSections = {
    [UserType.ARCHITECT_PROFESSIONAL]: [
      CompanyInfoSection,
      CompanyProfileSection,
      DesignOptionsSection,
      ProfessionalServicesSection,
    ],
    [UserType.PROPERTY_DEVELOPER]: [
      CompanyInfoSection,
      CompanyProfileSection,
      DesignOptionsSection,
      ProfessionalServicesSection,
    ],
    [UserType.SELF_BUILDER]: [PlotInformationSection],
    [UserType.OFFSITE_SUPPLIER]: [
      OffsiteCompanyProfileSection,
      DesignScopeSection,
      HouseQueriesSection,
      ProfessionalServicesSection,
      KeyInformationSection,
    ],
  };

  return (
    <div className={css.desktopReviews}>
      <div className={css.desktopReviewsWrapper}>
        <ButtonTabNavHorizontal
          isProfileTabs={true}
          className={css.desktopReviewsTabNav}
          tabs={desktopReviewTabs}
        />
        <div className={css.desktopReviewTabsContent}>
          {isAboutSelected && (
            <div className={css.aboutInfoTabContent}>
              <div className={css.infoBlock}>
                <h2>
                  <FormattedMessage id="ProfilePage.contact" />
                </h2>
                <ContactInfoSection {...commonProps} />
              </div>
              {/* User-specific sections */}
              {userTypeSections[userType]?.map((SectionComponent, index) => (
                <SectionComponent key={index} {...commonProps} />
              ))}
              {calendlyLink && (
                <div className={css.infoBlock}>
                  <h2>
                    <FormattedMessage id="ProfilePage.scheduleMeeting" />
                  </h2>
                  <div className={css.scheduleMeeting}>
                    <p>
                      <FormattedMessage id="ProfilePage.scheduleMeetingText" />
                    </p>
                    <ExternalLink href={calendlyLink} className={css.scheduleMeetingLink}>
                      <FormattedMessage id="ProfilePage.scheduleMeetingButton" />
                    </ExternalLink>
                  </div>
                </div>
              )}
            </div>
          )}

          {isListingsSelected && hasListings ? (
            <div className={listingsContainerClasses}>
              <H4 as="h2" className={css.listingsTitle}>
                <FormattedMessage
                  id="ProfilePage.listingsTitle"
                  values={{ count: listings.length }}
                />
              </H4>
              <ul className={css.listings}>
                {listings.map(l => (
                  <li className={css.listing} key={l.id.uuid}>
                    <ListingCard listing={l} showAuthorInfo={false} />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
          {isReviewTypeProviderSelected ? (
            <Reviews reviews={reviewsOfProvider} />
          ) : (
            <Reviews reviews={reviewsOfCustomer} />
          )}
        </div>
      </div>
    </div>
  );
};

export const MainContent = props => {
  const {
    userShowError,
    bio,
    displayName,
    listings,
    queryListingsError,
    reviews,
    queryReviewsError,
    viewport,
    publicData,
  } = props;
  const { userType } = publicData || {};
  const hasListings = listings.length > 0;
  const isMobileLayout = viewport.width < MAX_MOBILE_SCREEN_WIDTH;
  const hasBio = !!bio;

  const listingsContainerClasses = classNames(css.listingsContainer, {
    [css.withBioMissingAbove]: !hasBio,
  });

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }
  return (
    <div>
      <H2 as="h1" className={css.desktopHeading}>
        <FormattedMessage id="ProfilePage.desktopHeading" values={{ name: displayName }} />
      </H2>
      {hasBio ? <p className={css.bio}>{bio}</p> : null}

      <DesktopReviews
        reviews={reviews}
        queryReviewsError={queryReviewsError}
        publicData={publicData}
        hasListings={hasListings}
        listings={listings}
        listingsContainerClasses={listingsContainerClasses}
      />
      {/* {isMobileLayout ? (
        <MobileReviews reviews={reviews} queryReviewsError={queryReviewsError} />
      ) : (
        <DesktopReviews reviews={reviews} queryReviewsError={queryReviewsError} />
      )} */}
    </div>
  );
};

const ProfilePageComponent = props => {
  const config = useConfiguration();
  const { scrollingDisabled, currentUser, userShowError, user, intl, ...rest } = props;
  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const profileUser = ensureUser(user);
  const isCurrentUser =
    ensuredCurrentUser.id && profileUser.id && ensuredCurrentUser.id.uuid === profileUser.id.uuid;
  const { bio, displayName, publicData = {} } = profileUser?.attributes?.profile || {};
  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  if (userShowError && userShowError.status === 404) {
    return <NotFoundPage />;
  }
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        name: schemaTitle,
      }}
    >
      <LayoutSideNavigation
        sideNavClassName={css.aside}
        topbar={<TopbarContainer currentPage="ProfilePage" />}
        sideNav={
          <AsideContent user={user} isCurrentUser={isCurrentUser} displayName={displayName} />
        }
        footer={<FooterContainer />}
      >
        <MainContent
          bio={bio}
          displayName={displayName}
          publicData={publicData}
          userShowError={userShowError}
          {...rest}
        />
      </LayoutSideNavigation>
    </Page>
  );
};

ProfilePageComponent.defaultProps = {
  currentUser: null,
  user: null,
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
};

ProfilePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  user: propTypes.user,
  userShowError: propTypes.error,
  queryListingsError: propTypes.error,
  listings: arrayOf(propTypes.listing).isRequired,
  reviews: arrayOf(propTypes.review),
  queryReviewsError: propTypes.error,

  // form withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews,
    queryReviewsError,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;
  const listings = getMarketplaceEntities(state, userListingRefs);
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    user,
    userShowError,
    queryListingsError,
    listings,
    reviews,
    queryReviewsError,
  };
};

const ProfilePage = compose(
  connect(mapStateToProps),
  withViewport,
  injectIntl
)(ProfilePageComponent);

export default ProfilePage;
